import React from "react";
import { useTranslation } from "react-i18next";
import type { TransformedCartItem } from "../../types/cartApiTypes";

interface CartItemRowProps {
  item: TransformedCartItem;
  isProcessing: boolean;
  onQuantityChange: (key: string, newQuantity: number) => void;
  onRemoveItem: (key: string) => void;
  currencySymbol: string;
}

const CartItemRow: React.FC<CartItemRowProps> = ({
  item,
  isProcessing,
  onQuantityChange,
  onRemoveItem,
  currencySymbol,
}) => {
  const { t } = useTranslation();

  const min = item.quantityLimits.minimum;
  const max = item.quantityLimits.maximum;
  const step = item.quantityLimits.multiple_of;
  const canDecrease = item.quantity > min;
  const canIncrease = max <= 0 || item.quantity < max;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      onQuantityChange(item.key, value);
    }
  };

  return (
    // The main layout changes will be handled by CSS targeting this class
    <div className="cart-item-row">
      {/* Thumbnail */}
      <div className="item-thumbnail">
        {item.images.length > 0 && (
          <a href={item.permalink}>
            <img
              src={item.images[0].thumbnail}
              alt={item.images[0].alt || item.name}
            />
          </a>
        )}
      </div>

      {/* Name and Attributes */}
      <div className="item-name">
        <a href={item.permalink}>{item.name}</a>
        {item.attributes.length > 0 && (
          <div className="cart-item-attributes">
            {item.attributes.map((attribute) => {
              const term = attribute.term;
              const displayType = attribute.displayType || "button-text";
              return (
                <div
                  key={attribute.id}
                  className={`cart-item-attribute type-${displayType}`}
                >
                  <span className="attribute-name">{attribute.name}:</span>
                  {(() => {
                    // Switch statement remains the same
                    switch (displayType) {
                      case "color":
                        return (
                          <span
                            className="cart-item-color-swatch"
                            style={{
                              backgroundColor: term.color || "#CCCCCC",
                            }}
                            title={term.name}
                            aria-label={`${attribute.name}: ${term.name}`}
                          ></span>
                        );
                      case "button-text":
                        return (
                          <span className="cart-item-text-button">
                            {term.name}
                          </span>
                        );
                      case "button-image":
                        return term.buttonImage?.thumbnail ? (
                          <img
                            src={term.buttonImage.thumbnail}
                            alt={term.name}
                            className="cart-item-image-preview"
                            title={term.name}
                          />
                        ) : (
                          <span className="cart-item-text-button">
                            {term.name}
                          </span>
                        );
                      default:
                        return <span>{term.name}</span>; // Fallback
                    }
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="item-price">
        <span className="item-label-mobile">
          {t("cart.item.price", "Price")}:{" "}
        </span>
        {item.formattedPrice}
        {currencySymbol}
      </div>

      {/* Quantity */}
      <div className="item-quantity">
        <span className="item-label-mobile">
          {t("cart.item.quantity", "Quantity")}:{" "}
        </span>
        <div className="quantity-input-inline">
          <button
            className="quantity-button decrease"
            onClick={() => onQuantityChange(item.key, item.quantity - step)}
            disabled={!canDecrease || isProcessing}
            aria-label={t("cart.item.decreaseQuantity", "Decrease quantity")}
          >
            -
          </button>
          <input
            type="number"
            className="quantity-input-field"
            value={item.quantity}
            onChange={handleInputChange}
            min={min}
            max={max > 0 ? max : undefined}
            step={step}
            disabled={isProcessing}
            aria-label={t("cart.item.quantity", "Quantity")}
            pattern="[0-9]*"
          />
          <button
            className="quantity-button increase"
            onClick={() => onQuantityChange(item.key, item.quantity + step)}
            disabled={!canIncrease || isProcessing}
            aria-label={t("cart.item.increaseQuantity", "Increase quantity")}
          >
            +
          </button>
        </div>
      </div>

      {/* Subtotal */}
      <div className="item-subtotal">
        <span className="item-label-mobile">
          {t("cart.item.subtotal", "Subtotal")}:{" "}
        </span>
        {item.totals.formattedLineSubtotal}
        {currencySymbol}
      </div>

      {/* Remove Button */}
      <div className="item-remove">
        <button
          onClick={() => onRemoveItem(item.key)}
          disabled={isProcessing}
          aria-label={t("cart.item.remove", "Remove item")}
          className="remove-item-button"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default CartItemRow;
